import { type ComponentProps, type FunctionComponent, useMemo } from 'react'
import { RewardsIcons } from '@/src/experiences/rewards/icons'
import type { LinkProps } from 'expo-router'
import { Routes } from '@/src/common/enums/routes'
import type { TouchableOpacityProps } from 'react-native'
import { Image } from 'expo-image'
import { CashbackPreset } from '@/src/experiences/rewards/icons/CashbackPreset'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { isProduction } from '@/src/common/environment'
import { router } from 'expo-router'
import { Hooks } from '@/src/experiences/points-to-statement-credit'
import {
  PointsToStatementCreditSchema,
  usePointsToStatementCreditDynamicConfig,
} from '@/src/experiences/points-to-statement-credit/statsig/dynamic-config/usePointsToStatementCreditDynamicConfig'
import {
  RewardsControlSchema,
  useRewardsControlDynamicConfig,
} from '@/src/experiences/rewards/statsig/dynamic-config/useRewardsControlDynamicConfig'
import { useSourcePartnerName } from '@/src/common/hooks/useSourcePartnerName'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { sentryCaptureException } from '@/src/common/hooks/useLogError'

export interface RewardsActionBase {
  testID: NonNullable<TouchableOpacityProps['testID']>
  label: string
  initializing?: boolean
}

export interface RewardsActionWithOnPress
  extends RewardsActionBase,
    Pick<TouchableOpacityProps, 'onPress'> {
  Icon: FunctionComponent<{ width: string; height: string }>
  disabled?: never
}

export interface RewardsActionWithDisabled
  extends RewardsActionBase,
    Pick<TouchableOpacityProps, 'onPress'> {
  Icon: FunctionComponent<{ width: string; height: string; grayscale?: boolean }>
  disabled: boolean | 'isComingSoon' | 'partnerDisabled'
}

export interface RewardsActionWithRedemptionPortal {
  redemptionPortal: { title: string; path: string }
}

export interface RewardsActionWithRoute {
  route: LinkProps['href']
}

export type RewardsAction = (RewardsActionWithOnPress | RewardsActionWithDisabled) &
  (RewardsActionWithRedemptionPortal | RewardsActionWithRoute)

export type RewardsActionSection = readonly [rowLabel: string, rowActions: RewardsAction[]]

// TODO: move this to a dynamic config in statsig
// however it should be fine to do this isProduction-based branching for now because it gets compiled away
// https://docs.expo.dev/guides/tree-shaking/#remove-development-only-code
const airbnbPath = isProduction
  ? '/products/gift-cards/1cb05c94-36df-4858-a2d5-a2508e1849b8'
  : '/products/gift-cards/353acac5-4b2e-4997-94a9-cb97cd65f690'
const lowesPath = isProduction
  ? '/products/gift-cards/a481d78a-c369-448e-abf4-69cce831b87c'
  : '/products/gift-cards/b6bb5778-dfb6-4e58-9d40-6533997af3d0'

const amazonPath = isProduction
  ? '/products/gift-cards/511d61a4-1b78-4206-a037-dff01241b14f'
  : '/products/gift-cards/b96b3d3b-14c5-47d2-a3eb-45d10003c155'

function AirbnbIcon() {
  return (
    <Image
      source="https://assets.mesamember.com/rewards/airbnb.png"
      style={{ width: '100%', height: '100%', borderRadius: 8 }}
    />
  )
}

function LowesIcon() {
  return (
    <Image
      source="https://assets.mesamember.com/rewards/lowes_giftcard.png"
      style={{ width: '100%', height: '100%', borderRadius: 8 }}
    />
  )
}

function AmazonIcon() {
  return (
    <Image
      source="https://assets.mesamember.com/rewards/amazon_giftcard.png"
      style={{ width: '100%', height: '100%', borderRadius: 8 }}
    />
  )
}

const createCashbackPresetIcon =
  (preset: number) => (props: ComponentProps<typeof CashbackPreset>) => (
    <CashbackPreset children={`$${preset}`} {...props} />
  )

const CreditAmountIfRedeeming20 = 20
const CreditAmountIfRedeeming50 = 50

const FALLBACK_DOLLARS_PER_POINT_CONVERSION = 0.006

const CashbackPreset20Icon = createCashbackPresetIcon(CreditAmountIfRedeeming20)
const CashbackPreset50Icon = createCashbackPresetIcon(CreditAmountIfRedeeming50)

export function useRewardsActions() {
  const Alert = useAlert(useRewardsActions.name)
  const { sourcePartnerName } = useSourcePartnerName()
  const pointsTransferAscendaEnabled = useFeatureGate(FeatureGates.PointsTransferAscendaEnabled)
  const pointsToCashForMortgageEnabled = useFeatureGate(FeatureGates.PointsToCashForMortgageEnabled)

  const dynamicConfigResult = usePointsToStatementCreditDynamicConfig()
  const rewardsControlConfig = useRewardsControlDynamicConfig()
  const rewardsControl = useMemo(() => {
    if (rewardsControlConfig.loadingStatus !== 'Ready') return null
    try {
      return RewardsControlSchema.parse(rewardsControlConfig.dynamicConfig.value)
    } catch (error) {
      sentryCaptureException('Failed to parse rewards control config:', error)
      return null
    }
  }, [rewardsControlConfig])
  const { preparePresetRedemption } = Hooks.usePresetRedemption()

  const pointsTransferOmniEnabled = useFeatureGate(FeatureGates.PointsTransferOmniEnabled)
    .featureGate?.value

  const dynamicConfigValue =
    dynamicConfigResult.loadingStatus === 'Ready' ? dynamicConfigResult.dynamicConfig.value : null

  const dollarsPerPointConversion = useMemo(() => {
    if (!dynamicConfigValue) return FALLBACK_DOLLARS_PER_POINT_CONVERSION
    try {
      const parsedConfig = PointsToStatementCreditSchema.parse(dynamicConfigValue)
      return parsedConfig.statement_credit_cents_per_point
    } catch (error) {
      sentryCaptureException('Failed to parse statement credit config:', error)
      return FALLBACK_DOLLARS_PER_POINT_CONVERSION
    }
  }, [dynamicConfigValue])

  const { remainingPoints: remainingPointsIfRedeeming20 } = Hooks.usePointsRedemption({
    creditAmount: CreditAmountIfRedeeming20,
    dollarsPerPointConversion,
  })
  const { remainingPoints: remainingPointsIfRedeeming50 } = Hooks.usePointsRedemption({
    creditAmount: CreditAmountIfRedeeming50,
    dollarsPerPointConversion,
  })

  return useMemo(() => {
    if (!rewardsControl) return []

    const giftCards = rewardsControl['Gift Cards']
    const statementCredit = rewardsControl['Statement Credit']
    const moreRewards = rewardsControl['More Rewards']

    const insufficientPointsToRedeemFor20 =
      typeof remainingPointsIfRedeeming20 === 'number' && remainingPointsIfRedeeming20 < 1
    const insufficientPointsToRedeemFor50 =
      typeof remainingPointsIfRedeeming50 === 'number' && remainingPointsIfRedeeming50 < 1

    return [
      ...(giftCards.enabled
        ? ([
            [
              'Gift Cards',
              [
                {
                  testID: 'LowesButton',
                  label: "Lowe's",
                  Icon: LowesIcon,
                  redemptionPortal: { title: 'Gift Cards', path: lowesPath },
                  disabled: !giftCards.buttons["Lowe's"],
                },
                {
                  testID: 'AmazonButton',
                  label: 'Amazon',
                  Icon: AmazonIcon,
                  redemptionPortal: { title: 'Gift Cards', path: amazonPath },
                  disabled: !giftCards.buttons.Amazon,
                },
                {
                  testID: 'AirbnbButton',
                  label: 'Airbnb',
                  Icon: AirbnbIcon,
                  redemptionPortal: { title: 'Gift Cards', path: airbnbPath },
                  disabled: !giftCards.buttons.Airbnb,
                },
                {
                  testID: 'GiftCardsButton',
                  label: 'View More',
                  Icon: RewardsIcons.GiftCards,
                  redemptionPortal: { title: 'Gift Cards', path: '/products/gift-cards' },
                  disabled: !giftCards.buttons['View More'],
                },
              ],
            ],
          ] as const satisfies RewardsActionSection[])
        : []),
      ...(statementCredit.enabled
        ? ([
            [
              'Statement Credit',
              [
                {
                  testID: 'StatementCredit20Button',
                  label: 'Redeem $20',
                  disabled:
                    !statementCredit.buttons['Redeem $20'] ||
                    dynamicConfigResult.loadingStatus !== 'Ready' ||
                    remainingPointsIfRedeeming20 === 'loading' ||
                    remainingPointsIfRedeeming20 < 1,
                  Icon: CashbackPreset20Icon,
                  route: {
                    pathname: Routes.StatementCreditRedemptionConfirmation.route,
                    params: { creditAmount: CreditAmountIfRedeeming20 },
                  },
                  ...(insufficientPointsToRedeemFor20
                    ? {
                        onPress() {
                          Alert.alert(
                            'Insufficient Points',
                            "You don't have enough points to redeem $20.",
                            [
                              {
                                text: 'Redeem Custom Amount',
                                onPress() {
                                  router.push(Routes.PointsToStatementCredit.route)
                                },
                              },
                              { text: 'OK', style: 'cancel' },
                            ],
                          )
                        },
                      }
                    : {
                        onPress() {
                          preparePresetRedemption({
                            creditAmount: CreditAmountIfRedeeming20,
                            dollarsPerPointConversion,
                            remainingPoints: remainingPointsIfRedeeming20,
                          })
                        },
                      }),
                },
                {
                  testID: 'StatementCredit50Button',
                  label: 'Redeem $50',
                  disabled:
                    !statementCredit.buttons['Redeem $50'] ||
                    dynamicConfigResult.loadingStatus !== 'Ready' ||
                    remainingPointsIfRedeeming50 === 'loading' ||
                    remainingPointsIfRedeeming50 < 1,
                  Icon: CashbackPreset50Icon,
                  route: {
                    pathname: Routes.StatementCreditRedemptionConfirmation.route,
                    params: { creditAmount: CreditAmountIfRedeeming50 },
                  },
                  ...(insufficientPointsToRedeemFor50
                    ? {
                        onPress() {
                          Alert.alert(
                            'Insufficient Points',
                            "You don't have enough points to redeem $50.",
                            [
                              {
                                text: 'Redeem Custom Amount',
                                onPress() {
                                  router.push(Routes.PointsToStatementCredit.route)
                                },
                              },
                              { text: 'OK', style: 'cancel' },
                            ],
                          )
                        },
                      }
                    : {
                        onPress() {
                          preparePresetRedemption({
                            creditAmount: CreditAmountIfRedeeming50,
                            dollarsPerPointConversion,
                            remainingPoints: remainingPointsIfRedeeming50,
                          })
                        },
                      }),
                },
                {
                  testID: 'StatementCreditButton',
                  label: 'Custom Amount',
                  Icon: RewardsIcons.Cashback,
                  route: Routes.PointsToStatementCredit.route,
                  disabled:
                    !statementCredit.buttons['Custom Amount'] ||
                    dynamicConfigResult.loadingStatus !== 'Ready',
                },
              ],
            ],
          ] as const satisfies RewardsActionSection[])
        : []),
      ...(moreRewards.enabled
        ? ([
            [
              'More Rewards',
              [
                {
                  testID: 'MortgageButton',
                  label: 'Mortgage',
                  Icon: RewardsIcons.Mortgage,
                  route: Routes.PointsToCashForMortgage.route,
                  disabled:
                    !moreRewards.buttons.Mortgage ||
                    (pointsToCashForMortgageEnabled.loadingStatus !== 'Ready'
                      ? true
                      : pointsToCashForMortgageEnabled.featureGate.value
                        ? false
                        : 'isComingSoon'),
                },
                {
                  testID: 'PointsTransferOmniButton',
                  label: 'Omni',
                  Icon: RewardsIcons.PointsTransfer,
                  route: Routes.PartnerDetail.route,
                  disabled:
                    !moreRewards.buttons.Omni ||
                    (pointsTransferOmniEnabled ? false : ('isComingSoon' as const)),
                },
                {
                  testID: 'BookTravelButton',
                  label: 'Book Travel',
                  Icon: RewardsIcons.BookTravel,
                  redemptionPortal: { title: 'Flights', path: '/travel/flights' },
                  disabled:
                    !moreRewards.buttons['Book Travel'] ||
                    (sourcePartnerName === 'lakeview' ? ('partnerDisabled' as const) : false),
                },
                {
                  testID: 'PointsTransferButton',
                  label: 'Points Transfer',
                  Icon: RewardsIcons.PointsTransfer,
                  route: Routes.PointsTransferInterstitial.route,
                  disabled:
                    !moreRewards.buttons['Points Transfer'] ||
                    (sourcePartnerName === 'lakeview' || sourcePartnerName === 'carrington'
                      ? 'partnerDisabled'
                      : pointsTransferAscendaEnabled.loadingStatus !== 'Ready'
                        ? true
                        : pointsTransferAscendaEnabled.featureGate.value
                          ? false
                          : 'isComingSoon'),
                },
              ],
            ],
          ] as const satisfies RewardsActionSection[])
        : []),
    ] as const satisfies RewardsActionSection[]
  }, [
    pointsTransferAscendaEnabled,
    pointsToCashForMortgageEnabled,
    remainingPointsIfRedeeming20,
    remainingPointsIfRedeeming50,
    sourcePartnerName,
    Alert,
    dollarsPerPointConversion,
    preparePresetRedemption,
    dynamicConfigResult.loadingStatus,
    pointsTransferOmniEnabled,
    rewardsControl,
  ])
}
